import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import RangeChart from "./RangeChart";
import { flagBand, nameWithTag, readingTag } from "./data";
import { SENTINEL_DOC_IDS } from "./DigitiseModal";
import ModalPortal from "../common/ModalPortal";
import TruncatedText from "../common/TruncatedText";
import LabInvestigationService from "./LabInvestigationService";
import DigitisationService from "./DigitisationService";
import { fetchUnitOptions, getCachedUnitOptions } from "./LabUnitsService";
import { LI_V2_REFRESH } from "./events";

/**
 * "View trend" modal for a single lab test. Frontend-derived from the flat
 * investigation list — filter to entries sharing the same identity (sltCode
 * if available, else name), sort by testDate, render:
 *   • Header — test name + latest flag
 *   • Stats — TOTAL / FIRST / LATEST
 *   • Chart — SVG line chart with reference-range band (excludes marked-incorrect)
 *   • Latest reported value strip
 *   • Audit table — test date · value · unit · ref. range · status · tested at · correction flag
 *
 * Correction flow (mirrors Dx v2's flag pattern):
 *   • Gray flag  → Review dialog (Correct / Mark incorrect)
 *   • Amber flag → Correction history popup (Original vs Latest)
 *   • Red flag   → Marked-incorrect audit card
 * Correction applies to the LATEST audit entry of the picked investigation
 * (backend endpoint `/history/:index/{incorrect,correct}` — see
 * `LabInvestigationService.markIncorrect / correctHistory`).
 */

// Source cell display: numeric / real Document IDs are rendered as
// "Document <id>"; the three sentinel selections keep their friendly
// label; empty falls back to em-dash. Kept as a plain-string helper
// for places that don't want React nodes (e.g. the latest-value strip).
const SENTINEL_LABEL = SENTINEL_DOC_IDS.reduce(
  (acc, s) => { acc[s.value] = s.label; return acc; },
  {},
);
const formatSource = (docId) => {
  const s = String(docId || "").trim();
  if (!s) return "—";
  if (SENTINEL_LABEL[s]) return SENTINEL_LABEL[s];
  return `Document ${s}`;
};

// SourceCell — same display as formatSource, but real Document IDs
// render as a link that opens the source document in a new tab (when
// a viewable URL is known). Sentinels + unknown / non-linkable docs
// fall back to plain text.
const SourceCell = ({ docId, docs, docToken }) => {
  const s = String(docId || "").trim();
  if (!s) return "—";
  if (SENTINEL_LABEL[s]) return SENTINEL_LABEL[s];
  const doc = (docs || []).find((d) => String(d.id) === s);
  const rawUrl = doc && doc.url;
  if (!rawUrl) return `Document ${s}`;
  const sep = rawUrl.includes("?") ? "&" : "?";
  const href = docToken ? `${rawUrl}${sep}token=${encodeURIComponent(docToken)}` : rawUrl;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="li-corr-doc-link"
      title="Open source document in a new tab"
    >
      Document {s}
    </a>
  );
};
SourceCell.propTypes = {
  docId: PropTypes.string,
  docs: PropTypes.array,
  docToken: PropTypes.string,
};

// Each `row` in TrendModal is a synthetic entry — see the row builder in
// LabInvestigations.openTrend. It flattens each Investigation's
// statusHistory[] into one row per audit entry, tagging the row with
// `_origInvestigationId`, `_historyIndex`, `_docVersion` plus the
// audit-entry's own markedIncorrect / correction state. Correction flow
// operates on the audit entry addressed by (_origInvestigationId,
// _historyIndex).
const rowStateOf = (row) => ({
  invId: row && row._origInvestigationId,
  historyIndex: row && row._historyIndex != null ? row._historyIndex : -1,
  docVersion: (row && row._docVersion) || 0,
  markedIncorrect: !!(row && row.markedIncorrect),
  correction: (row && row.correction) || null,
});

// "27 Jul 2026, 01:05 pm" — matches Dx v2's mark-incorrect card format.
const dateAndTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${date}, ${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

const actorLabel = (actor, name) => {
  if (name) return name;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

// Flag icon — matches Dx v2's visual language. `active` = flag is either
// filled red (corrected OR marked-incorrect); false = gray outline.
// Both corrected and incorrect share the SAME red flag; the difference
// is in which popup opens on click.
const FlagIcon = ({ active }) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M3.5 2.25v11.5"
      stroke={active ? "#b91c1c" : "#6a7282"}
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M3.5 2.5h7.75l-1.25 2.5 1.25 2.5H3.5"
      fill={active ? "#b91c1c" : "transparent"}
      stroke={active ? "#b91c1c" : "#6a7282"}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);
FlagIcon.propTypes = { active: PropTypes.bool };

// Wrapper button — sizes + click affordance for the flag icon in the audit
// grid. Kept separate so the same FlagIcon can be reused in the success
// dialog + marked-incorrect card without a button chrome.
const FlagButton = ({ active, onClick, title }) => (
  <button
    type="button"
    className="li-corr-flag"
    onClick={onClick}
    aria-label={title}
    title={title}
  >
    <FlagIcon active={active} />
  </button>
);
FlagButton.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
};

// ── Review dialog: 3-way choice pointing at one row ─────────────────────
const ReviewDialog = ({ onCorrect, onMarkIncorrect, onClose }) => (
  <div className="cc-modal-backdrop li-corr-overlay" onMouseDown={onClose}>
    <div className="li-corr-dlg" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
      <button type="button" className="li-corr-dlg-x" onClick={onClose} aria-label="Close">×</button>
      <h3 className="li-corr-dlg-title">Review this entry</h3>
      <p className="li-corr-dlg-body">
        You can correct this result's details — value, unit or test date — or
        mark the whole result as incorrect. Corrected results keep their
        history; results marked incorrect are excluded from trend graphs.
      </p>
      <div className="li-corr-dlg-btns">
        <button type="button" className="li-corr-btn li-corr-btn-danger" onClick={onMarkIncorrect}>
          Mark as incorrect
        </button>
        <button type="button" className="li-corr-btn li-corr-btn-primary" onClick={onCorrect}>
          Yes, correct entry
        </button>
      </div>
    </div>
  </div>
);
ReviewDialog.propTypes = {
  onCorrect: PropTypes.func.isRequired,
  onMarkIncorrect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ── Success dialog — post-action ack for BOTH corrected + marked-incorrect
// paths. Same visual (red flag medallion, title, body, Done button); copy
// varies by mode. Matches Dx v2's SuccessDialog.
const SuccessDialog = ({ mode, onClose }) => (
  <div className="cc-modal-backdrop li-corr-overlay" onMouseDown={onClose}>
    <div className="li-corr-dlg li-corr-dlg-center" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
      <button type="button" className="li-corr-dlg-x" onClick={onClose} aria-label="Close">×</button>
      <div className="li-corr-ack-flag" aria-hidden="true">
        <FlagIcon active />
      </div>
      <h3 className="li-corr-dlg-title li-corr-dlg-title-center">
        {mode === "incorrect" ? "Result marked as incorrect" : "Entry corrected"}
      </h3>
      <p className="li-corr-dlg-body li-corr-dlg-body-center">
        {mode === "incorrect"
          ? "This result is excluded from trends and graphs. It remains in the audit history."
          : "The audit entry now shows the corrected values. Its previous values are preserved in the correction history."}
      </p>
      <button type="button" className="li-corr-btn li-corr-btn-primary li-corr-btn-full" onClick={onClose}>
        Done
      </button>
    </div>
  </div>
);
SuccessDialog.propTypes = {
  mode: PropTypes.oneOf(["incorrect", "corrected"]).isRequired,
  onClose: PropTypes.func.isRequired,
};

// ── Correction form ─────────────────────────────────────────────────────
// Range + Unit options — mirror the DigitiseManually picker so the
// reviewer has the same vocabulary in both places. Kept local so a
// future divergence between add-time and correction-time is easy.
const RANGE_OPTIONS = [
  { value: "",         label: "Select" },
  { value: "none",     label: "None" },
  { value: "normal",   label: "Normal" },
  { value: "abnormal", label: "Abnormal" },
  { value: "high",     label: "High" },
  { value: "medium",   label: "Medium" },
  { value: "low",      label: "Low" },
];
// Reads any stored flag ("normal", "high", …) back to the picker label
// ("Normal", "High", …). Keeps the correction-history dialog in sync
// with the correction dropdown so users see the same vocabulary they
// picked. Blank / "unknown" / missing → em-dash.
const formatRangeLabel = (flag) => {
  const key = (flag || "").toString().toLowerCase();
  if (!key || key === "unknown") return "—";
  const hit = RANGE_OPTIONS.find((o) => o.value === key);
  return hit ? hit.label : flag;
};
// Unit list — fetched via LabUnitsService (module-level cache shared
// with the Digitise-manually modal). The CorrectionForm subscribes on
// mount so the picker fills in as soon as the network call lands.

// Local timezone `YYYY-MM-DD` — matches the native <input type="date">
// value format so `max={todayISO()}` blocks tomorrow / future dates.
const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const CorrectionForm = ({ testName, initial, onCancel, onSave, saving }) => {
  const [testDate, setTestDate] = useState(initial.testDate || "");
  const [value, setValue] = useState(initial.value || "");
  const [unit, setUnit] = useState(initial.unit || "");
  // Seed Range from the current entry so the picker opens on the
  // reviewer's existing classification. Empty falls back to "Select"
  // (which lets the server compute the flag from value + ref range).
  const [range, setRange] = useState(initial.range || "");
  // ZY-10340: description is editable on correction too. Seed from the
  // corrected entry so the reviewer sees + can amend the existing note.
  const [description, setDescription] = useState(initial.description || "");
  // Unit picker corpus — seeded from the module-level cache; refresh on
  // mount when the cache is cold so the first-opened dialog primes it
  // for every subsequent open.
  const [unitOptions, setUnitOptions] = useState(() => getCachedUnitOptions() || []);
  useEffect(() => {
    fetchUnitOptions().then((rows) => {
      if (rows && rows.length) setUnitOptions(rows);
    });
  }, []);
  // Belt-and-braces guard on the date — the input's `max` + onChange
  // clamp cover typing / picking, but a stale form value opened yesterday
  // could still be tomorrow now. Refuse to save in that case.
  const dateIsPastOrToday = testDate && String(testDate).slice(0, 10) <= todayISO();
  // ZY-10340: at least one of {value, description} required — same rule
  // the backend enforces via ValidateValueOrDescription.
  const hasValue = !!String(value).trim();
  const hasDesc = !!String(description).trim();
  // Dirty-tracking: Save enables only when at least one field differs
  // from the initial (seeded) value. Prevents a no-op save that would
  // otherwise write a new correction row identical to the entry being
  // corrected. Normalise both sides before comparing:
  //   • dates → YYYY-MM-DD slice so an ISO seed ("2026-09-25T…") lines
  //     up with the input's date-string ("2026-09-25")
  //   • text  → trim, so trailing whitespace doesn't count as a change
  const dNorm = (d) => (d ? String(d).slice(0, 10) : "");
  const tNorm = (s) => String(s || "").trim();
  const isDirty =
    dNorm(testDate)    !== dNorm(initial.testDate) ||
    tNorm(value)       !== tNorm(initial.value) ||
    tNorm(unit)        !== tNorm(initial.unit) ||
    tNorm(range)       !== tNorm(initial.range) ||
    tNorm(description) !== tNorm(initial.description);
  const canSave = !!dateIsPastOrToday && (hasValue || hasDesc) && isDirty && !saving;
  const submit = () => {
    if (!canSave) return;
    const numericValue = Number(String(value).replace(/[^0-9.-]/g, ""));
    onSave({
      testDate: new Date(testDate).toISOString(),
      value: String(value).trim(),
      numericValue: isNaN(numericValue) ? undefined : numericValue,
      unit: unit.trim(),
      // Empty range → server keeps the existing / computed flag.
      // Any non-empty option is sent verbatim; the backend validates
      // against constants.IsValidFlag before persisting.
      flag: range,
      description: String(description).trim(),
    });
  };
  return (
    <div className="cc-modal-backdrop li-corr-overlay" onMouseDown={onCancel}>
      <div className="li-corr-dlg li-corr-form" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="li-corr-form-head">
          <h3 className="li-corr-dlg-title">Correct entry &middot; {testName}</h3>
          <button type="button" className="li-corr-dlg-x" onClick={onCancel} aria-label="Close">×</button>
        </div>
        <label className="li-corr-field">
          <span>Investigation</span>
          <div className="li-corr-readonly">{testName}</div>
        </label>
        <div className="li-corr-two">
          <label className="li-corr-field">
            <span>Test date <span className="li-dm-req">*</span></span>
            {/* Test date bounds: 1900-01-01 → today. Same policy as
                DigitiseManuallyCard / LabInvestigationForm — pre-1900
                is almost always a typo and would break the trend
                chart's x-axis. */}
            <input
              type="date"
              value={testDate ? String(testDate).slice(0, 10) : ""}
              min="1900-01-01"
              max={todayISO()}
              onChange={(e) => {
                const v = e.target.value;
                let clamped = v;
                if (v && v > todayISO()) clamped = todayISO();
                else if (v && v < "1900-01-01") clamped = "1900-01-01";
                setTestDate(clamped);
              }}
              disabled={saving}
            />
          </label>
          <label className="li-corr-field">
            <span>Range <span className="li-dm-req">*</span></span>
            <span className="li-corr-select-wrap">
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                disabled={saving}
              >
                {RANGE_OPTIONS.map((o) => (
                  <option key={o.value || "unset"} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="li-corr-chev" aria-hidden="true">&#8964;</span>
            </span>
          </label>
        </div>
        <div className="li-corr-two">
          <label className="li-corr-field">
            <span>Value <span className="li-dm-req">*</span></span>
            {/* Correction dialog shares the same 100-char ceiling
                every other LI value input enforces (Digitise-card,
                LabInvestigationForm). Keeps the trend / table's
                truncation contract deterministic. */}
            <input
              type="text"
              value={value}
              maxLength={100}
              onChange={(e) => setValue((e.target.value || "").slice(0, 100))}
              disabled={saving}
            />
          </label>
          <label className="li-corr-field">
            <span>Unit <span className="li-dm-req">*</span></span>
            <span className="li-corr-select-wrap">
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                disabled={saving}
              >
                <option value="">Select</option>
                {unitOptions.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <span className="li-corr-chev" aria-hidden="true">&#8964;</span>
            </span>
          </label>
        </div>
        {/* Description (ZY-10340) — editable on correction, at-least-one
            rule mirrors the digitise + form gates. */}
        <label className="li-corr-field li-corr-field-desc">
          <span>Description <span className="li-dm-lbl-hint">&middot; optional</span></span>
          <textarea
            className="li-corr-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add description…"
            rows={3}
            disabled={saving}
          />
        </label>
        <div className="li-corr-dlg-btns">
          <button type="button" className="li-corr-btn li-corr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="li-corr-btn li-corr-btn-primary" onClick={submit} disabled={!canSave}>
            {saving ? "Saving…" : "Save correction"}
          </button>
        </div>
      </div>
    </div>
  );
};
CorrectionForm.propTypes = {
  testName: PropTypes.string.isRequired,
  initial: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

// ── Marked-incorrect audit card (red-flag click) ────────────────────────
const MarkedIncorrectCard = ({ entry, onClose }) => (
  <div className="cc-modal-backdrop li-corr-overlay" onMouseDown={onClose}>
    <div className="li-corr-mark" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
      <div className="li-corr-mark-head">
        <span className="li-corr-mark-eyebrow"><span className="li-corr-mark-flag" aria-hidden="true">⚑</span> MARKED INCORRECT</span>
        <button type="button" className="li-corr-dlg-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      <dl className="li-corr-mark-list">
        <dt>By</dt>
        <dd>{actorLabel(entry.markedIncorrectBy, entry.markedIncorrectByName)}</dd>
        <dt>On</dt>
        <dd>{entry.markedIncorrectAt ? formatShortDate(entry.markedIncorrectAt) : "—"}</dd>
        <dt>At</dt>
        <dd>{entry.markedIncorrectAt ? dateAndTime(entry.markedIncorrectAt).split(", ")[1] : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectCard.propTypes = { entry: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired };

// ── Correction history popup (amber-flag click) ─────────────────────────
const CorrectionHistoryDialog = ({ testName, entry, inv, unit, onClose }) => {
  const prev = entry.correction && entry.correction.previousSnapshot;
  const at = entry.correction && entry.correction.at;
  return (
    <div className="cc-modal-backdrop li-corr-overlay" onMouseDown={onClose}>
      <div className="li-corr-hist" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="li-corr-hist-head">
          <h3 className="li-corr-dlg-title">Correction history</h3>
          <button type="button" className="li-corr-dlg-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="li-corr-hist-meta">{testName} &middot; corrected {dateAndTime(at)}</p>

        <div className="li-corr-hist-block">
          <div className="li-corr-hist-block-head">
            <span>ORIGINAL ENTRY</span>
            <span className="li-corr-tag li-corr-tag-incorrect">Marked incorrect</span>
          </div>
          <dl className="li-corr-hist-list">
            <dt>Test date</dt><dd>{prev ? formatShortDate(prev.testDate) : "—"}</dd>
            <dt>Value</dt><dd>{(prev && prev.value) || "—"}</dd>
            <dt>Unit</dt><dd>{(prev && prev.unit) || "—"}</dd>
            <dt>Range</dt><dd>{formatRangeLabel(prev && prev.flag)}</dd>
            <dt>Description</dt><dd>{(prev && prev.description) || "—"}</dd>
          </dl>
        </div>

        <div className="li-corr-hist-block li-corr-hist-block-current">
          <div className="li-corr-hist-block-head">
            <span>LATEST ENTRY</span>
            <span className="li-corr-tag li-corr-tag-current">Current</span>
          </div>
          <dl className="li-corr-hist-list">
            <dt>Test date</dt><dd>{formatShortDate(entry.testDate)}</dd>
            <dt>Value</dt><dd>{entry.value || "—"}</dd>
            <dt>Unit</dt><dd>{(entry.test && entry.test.unit) || unit || "—"}</dd>
            <dt>Range</dt><dd>{formatRangeLabel(entry.flag)}</dd>
            <dt>Description</dt><dd>{entry.description || "—"}</dd>
          </dl>
        </div>

        <button type="button" className="li-corr-btn li-corr-btn-primary li-corr-btn-full" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
};
CorrectionHistoryDialog.propTypes = {
  testName: PropTypes.string.isRequired,
  entry: PropTypes.object.isRequired,
  inv: PropTypes.object.isRequired,
  unit: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const TrendModal = ({ patientId, test, entries, onClose }) => {
  const [dialog, setDialog] = useState(null); // { kind, row } — kind ∈ "review"|"form"|"ack"|"mark"|"hist"
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Documents + signed viewer token — used to turn "Document <id>" in
  // the Source column into a click-through link. Fetched once on mount;
  // failures degrade to plain text (no link). Empty docs list = every
  // Document ID renders as plain text — no crash, no missing rows.
  const [docs, setDocs] = useState([]);
  const [docToken, setDocToken] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!patientId) return undefined;
    let alive = true;
    const svc = new DigitisationService();
    svc.listDocuments(
      patientId,
      (data) => { if (alive) setDocs(Array.isArray(data) ? data : []); },
      () => { if (alive) setDocs([]); },
    );
    svc.getDocumentToken(
      (t) => { if (alive) setDocToken(t || ""); },
      () => {},
    );
    return () => { alive = false; };
  }, [patientId]);

  // Sorted by testDate ascending; latest at the end. Two rows on the
  // same testDate tiebreak on recordedAt (when the audit entry was
  // persisted) so the reviewer's LATEST reading sits at the tail —
  // otherwise same-date sorts are unstable and the trend chart / stats
  // / audit table could pick the wrong "latest".
  const sorted = useMemo(() => {
    const rank = (r) => {
      const t = r && r.recordedAt && new Date(r.recordedAt).getTime();
      return !isNaN(t) ? t : 0;
    };
    return (entries || [])
      .filter((e) => e && e.testDate)
      .map((e) => ({ ...e, _t: new Date(e.testDate).getTime() }))
      .filter((e) => !isNaN(e._t))
      .sort((a, b) => {
        const dt = a._t - b._t;
        return dt !== 0 ? dt : rank(a) - rank(b);
      });
  }, [entries]);

  // `visible` — rows that count toward TESTS / FIRST / LATEST + the chart
  // + the "live" flag suppression rule. Marked-incorrect rows are
  // excluded (they stay in the audit table as struck-through history
  // but don't contribute to derived stats or the current-value strip).
  // Corrections stay counted — the corrected value is the current state.
  const visible = useMemo(() => sorted.filter((e) => !e.markedIncorrect), [sorted]);
  // Audit table renders newest → oldest; `sorted` stays ascending because
  // FIRST / LATEST + the trend chart's ordering depend on it, and the
  // isLive check compares by id (order-independent).
  const displaySorted = useMemo(() => [...sorted].reverse(), [sorted]);
  const first = visible[0];
  const latest = visible[visible.length - 1];
  const latestFlag = latest ? latest.flag : "unknown";
  const band = flagBand(latestFlag);
  // Live row = the most-recent non-incorrect row (its flag column stays
  // empty — use Edit / Delete on the main list). Falls back to -1 when
  // every row is marked-incorrect (defensive; UI still safe).
  const liveKey = latest ? latest.investigationId : null;

  const unit = (test && test.unit) || "";
  // The title spans every reading, so it is the bare name: the investigation's
  // own test.tag is only the latest write's.
  const testName = (test && test.name) || "Test";
  // A Tag column only when some reading has one — untagged tests keep the
  // board's six columns.
  const hasTags = useMemo(() => sorted.some((e) => !!readingTag(e.test)), [sorted]);

  const closeDialog = () => { setDialog(null); setError(null); };

  const openFlag = (inv) => {
    const st = rowStateOf(inv);
    if (st.markedIncorrect) return setDialog({ kind: "mark", row: inv, state: st });
    if (st.correction) return setDialog({ kind: "hist", row: inv, state: st });
    return setDialog({ kind: "review", row: inv, state: st });
  };

  // Correction and MarkIncorrect target `statusHistory[i]` — a concurrent
  // append could shift `i` under us and retarget the wrong reading, so
  // strict OCC is required. `state.docVersion` was snapshotted at
  // section-list time and can go stale between opening the trend modal
  // and clicking submit; refetch the current version RIGHT BEFORE the
  // write so we always send the freshest number. Backend still rejects
  // the write if a concurrent editor slipped in during the round trip
  // (rare) — user sees the "version conflict" error and retries.

  const runMark = () => {
    const { row, state } = dialog;
    if (!row || !state.invId || state.historyIndex < 0) return;
    setSaving(true);
    setError(null);
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (freshDoc) => {
        const v = (freshDoc && freshDoc.version) || 0;
        svc.markIncorrect(
          patientId,
          state.invId,
          state.historyIndex,
          v,
          () => {
            setSaving(false);
            setDialog({ kind: "success", mode: "incorrect" });
            EventService.emit(LI_V2_REFRESH);
          },
          (err) => {
            setSaving(false);
            setError((err && err.message) || "Failed to mark as incorrect");
          },
        );
      },
      (err) => {
        setSaving(false);
        setError((err && err.message) || "Failed to mark as incorrect");
      },
    );
  };

  const runCorrection = (body) => {
    const { row, state } = dialog;
    if (!row || !state.invId || state.historyIndex < 0) return;
    setSaving(true);
    setError(null);
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (freshDoc) => {
        const v = (freshDoc && freshDoc.version) || 0;
        svc.correctHistory(
          patientId,
          state.invId,
          state.historyIndex,
          { ...body, expectedVersion: v },
          () => {
            setSaving(false);
            setDialog({ kind: "success", mode: "corrected" });
            EventService.emit(LI_V2_REFRESH);
          },
          (err) => {
            setSaving(false);
            setError((err && err.message) || "Failed to save correction");
          },
        );
      },
      (err) => {
        setSaving(false);
        setError((err && err.message) || "Failed to save correction");
      },
    );
  };

  // Chart uses the SAME `visible` set — a marked-incorrect outlier
  // shouldn't skew the trend line either.
  const chartData = visible;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal cc-modal-trend li-trend-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">{testName}</span>
              {latest && (
                <span
                  className="cc-modal-tag"
                  style={{ color: band.fg, background: band.bg }}
                >
                  {band.label}
                </span>
              )}
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>

          <div className="cc-modal-body">
            <div className="cc-trend-stats">
              <div>
                <span className="cc-trend-lbl">Total</span>
                <span className="cc-trend-val">{visible.length}</span>
              </div>
              <div>
                <span className="cc-trend-lbl">First</span>
                <span className="cc-trend-val">{first ? formatShortDate(first.testDate) : "—"}</span>
              </div>
              <div>
                <span className="cc-trend-lbl">Latest</span>
                <span className="cc-trend-val">{latest ? formatShortDate(latest.testDate) : "—"}</span>
              </div>
            </div>

            <div className="cc-trend-chart-wrap">
              <div className="cc-trend-chart-head">
                <span className="cc-trend-chart-title">Range over time</span>
                {first && latest && (
                  <span className="cc-trend-chart-range">
                    {formatShortDate(first.testDate)} – {formatShortDate(latest.testDate)}
                  </span>
                )}
              </div>
              <RangeChart entries={chartData} unit={unit} />
              {latest && (
                <p className="cc-trend-chart-latest">
                  Latest reported value:{" "}
                  <strong style={{ color: band.fg }}>
                    {latest.value}
                    {unit ? ` ${unit}` : ""}
                  </strong>
                  {readingTag(latest.test) ? ` · ${readingTag(latest.test)}` : ""}
                  {" · "}
                  {formatShortDate(latest.testDate)}
                  {latest.documentId ? ` · ${formatSource(latest.documentId)}` : ""}
                </p>
              )}
            </div>

            <div className="cc-trend-audit">
              <div className="cc-trend-audit-head">
                <span>Audit</span>
                <span className="cc-trend-audit-count">&middot; {sorted.length} entries</span>
              </div>
              <div className={`sec-table li-trend-grid${hasTags ? " has-tags" : ""}`} role="table">
                <div className="sec-thead" role="row">
                  <span role="columnheader">Test date</span>
                  {hasTags && <span role="columnheader">Tag</span>}
                  <span role="columnheader">Value</span>
                  <span role="columnheader">Unit</span>
                  <span role="columnheader">Range</span>
                  <span role="columnheader">Description</span>
                  <span role="columnheader">Source</span>
                  <span role="columnheader" aria-label="Correction"></span>
                </div>
                {displaySorted.map((e) => {
                  const st = rowStateOf(e);
                  const b = flagBand(e.flag || "unknown");
                  const rowUnit = (e.test && e.test.unit) || unit || "—";
                  // Same red flag for both corrected + marked-incorrect —
                  // matches Dx v2's visual language. Click behaviour
                  // differs (opens history vs audit card vs review).
                  const flagActive = st.markedIncorrect || !!st.correction;
                  const flagTitle = st.markedIncorrect
                    ? "Marked incorrect · click for audit"
                    : st.correction
                      ? "Corrected · click for history"
                      : "Review this entry";
                  // "Live" = most-recent NON-INCORRECT row (matches
                  // Allergy v2's rule). Live row's flag column stays
                  // empty — use Edit / Delete on the main list to
                  // change the live entry. Marked-incorrect rows are
                  // never live and always keep their flag (which opens
                  // the audit card, not review).
                  const isLive = e.investigationId === liveKey;
                  return (
                    <div
                      className={`sec-row li-trend-row${st.markedIncorrect ? " is-incorrect" : ""}${isLive ? " is-live" : ""}`}
                      role="row"
                      key={e.investigationId}
                    >
                      <span className="sec-cell" role="cell">{formatShortDate(e.testDate)}</span>
                      {hasTags && (
                        <span className="sec-cell li-audit-tag" role="cell">
                          {readingTag(e.test) || "—"}
                        </span>
                      )}
                      <span className="sec-cell li-audit-value" role="cell">
                        {/* Same TruncatedText clip + Show more chip
                            contract as the main LI table's Value
                            column — long readings drop the chip and
                            open the full string in a dialog. */}
                        <TruncatedText
                          text={e.value != null ? String(e.value) : ""}
                          title="Value"
                        />
                      </span>
                      <span className="sec-cell" role="cell">{rowUnit}</span>
                      <span className="sec-cell" role="cell" style={{ color: b.fg, fontWeight: 600 }}>
                        {b.label}
                      </span>
                      <span className="sec-cell li-audit-desc" role="cell">
                        <TruncatedText text={e.description} />
                      </span>
                      <span className="sec-cell" role="cell">
                        <SourceCell docId={e.documentId} docs={docs} docToken={docToken} />
                      </span>
                      <span className="sec-cell li-corr-cell" role="cell">
                        {!isLive && (
                          <FlagButton
                            active={flagActive}
                            onClick={() => openFlag(e)}
                            title={flagTitle}
                          />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {dialog && dialog.kind === "review" && (
        <ReviewDialog
          onCorrect={() => setDialog({ ...dialog, kind: "form" })}
          onMarkIncorrect={runMark}
          onClose={closeDialog}
        />
      )}
      {dialog && dialog.kind === "form" && (
        <CorrectionForm
          testName={nameWithTag(dialog.row.test, testName)}
          initial={{
            testDate: dialog.row.testDate,
            value: dialog.row.value,
            unit: (dialog.row.test && dialog.row.test.unit) || unit,
            // Seed the Range picker from the row's current flag. "unknown"
            // maps to empty so the picker shows "Select" (server will
            // recompute or keep whatever it had).
            range: dialog.row.flag && dialog.row.flag !== "unknown" ? dialog.row.flag : "",
            // ZY-10340: seed the description so the reviewer can amend
            // the existing note rather than re-typing from scratch.
            description: dialog.row.description || "",
          }}
          saving={saving}
          onCancel={closeDialog}
          onSave={runCorrection}
        />
      )}
      {dialog && dialog.kind === "success" && (
        <SuccessDialog mode={dialog.mode || "corrected"} onClose={closeDialog} />
      )}
      {dialog && dialog.kind === "mark" && dialog.row && (
        <MarkedIncorrectCard entry={dialog.row} onClose={closeDialog} />
      )}
      {dialog && dialog.kind === "hist" && dialog.row && (
        <CorrectionHistoryDialog
          testName={nameWithTag(dialog.row.test, testName)}
          entry={dialog.row}
          inv={dialog.row}
          unit={unit}
          onClose={closeDialog}
        />
      )}
      {error && (
        <div className="li-corr-toast" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  test: PropTypes.object.isRequired,
  entries: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
